// =============================================================================
// MeuDriver — Batch U4: Seletor de templates de resposta rápida no chat
// =============================================================================

import { useState, useRef, useEffect } from 'react'
import { FileText, ChevronDown, X } from 'lucide-react'
import { useInstructorChatTemplates } from '@/hooks/useChatTemplates'
import { cn } from '@/lib/utils'
import type { InstructorChatTemplateRow } from '@/types/database'

interface ChatTemplateSelectorProps {
  instructorId: string
  /** Chamado quando o usuário seleciona um template — preenchimento do input, não envio */
  onSelect: (body: string) => void
  className?: string
}

export function ChatTemplateSelector({
  instructorId,
  onSelect,
  className,
}: ChatTemplateSelectorProps) {
  const { data: templates = [], isLoading } = useInstructorChatTemplates(instructorId)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Sem templates ou carregando, não exibe o botão (sem templates cadastrados)
  if (!isLoading && templates.length === 0) return null

  function handleSelect(template: InstructorChatTemplateRow) {
    onSelect(template.body)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        data-testid="chat-template-btn"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
          open
            ? 'bg-brand-50 border-brand-300 text-brand-700'
            : 'bg-white border-gray-200 text-gray-600 hover:border-brand-300 hover:text-brand-600',
        )}
      >
        <FileText className="h-3.5 w-3.5" />
        Templates
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 z-50 w-72 rounded-xl border border-gray-200 bg-white shadow-lg dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Respostas rápidas
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800">
            {isLoading ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                Carregando...
              </div>
            ) : (
              templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  data-testid={`template-item-${template.id}`}
                  onClick={() => handleSelect(template)}
                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                    {template.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                    {template.body}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
